import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mapBankAccountFieldsBr,
  getInitialBankAccountFormMode,
  FORM_MODES,
} from '../../domain';

export const useBankAccountFormSyncBr = ({
  accounts,
  requiresCnpj,
  banksData,
  selectedBank,
  selectedAccountType,
  reset,
  setValue,
  clearErrors,
  isSubmitting,
  isSavingData,
  isLoadingExistingData,
  isLoadingDataBankList,
  setFormMode,
}) => {
  const { t } = useTranslation(['form']);
  const previousBankIdRef = useRef(null);
  const isSyncingRef = useRef(false);
  const previousAccountTypeValueRef = useRef(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (isLoadingExistingData || !banksData?.length) return;

    hasInitializedRef.current = true;

    const initialMode = getInitialBankAccountFormMode({
      accounts,
      requiresCnpj,
    });
    const mappedData = mapBankAccountFieldsBr({
      accounts,
      bankListData: banksData,
      formMode: initialMode,
      t,
    });

    isSyncingRef.current = true;
    previousBankIdRef.current = mappedData?.bank?.value ?? null;
    previousAccountTypeValueRef.current =
      mappedData?.accountType?.value ?? null;

    setFormMode(initialMode);
    reset(mappedData);

    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  }, [
    accounts,
    requiresCnpj,
    banksData,
    reset,
    setFormMode,
    isLoadingExistingData,
    t,
  ]);

  useEffect(() => {
    if (isSyncingRef.current) return;
    const currentBankId = selectedBank?.value;

    if (
      currentBankId !== previousBankIdRef.current &&
      previousBankIdRef.current !== null
    ) {
      setValue('accountType', null, { shouldDirty: true });
    }

    previousBankIdRef.current = currentBankId;
  }, [selectedBank, setValue]);

  const selectedAccountTypeValue = selectedAccountType?.value ?? null;

  useEffect(() => {
    if (isSyncingRef.current) return;
    if (selectedAccountTypeValue === null) return;
    if (selectedAccountTypeValue === previousAccountTypeValueRef.current)
      return;

    previousAccountTypeValueRef.current = selectedAccountTypeValue;

    setValue('accountType', selectedAccountType, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [selectedAccountTypeValue, setValue, selectedAccountType]);

  const handleNoCnpjChange = useCallback(
    (_, checked) => {
      if (requiresCnpj) return;

      const nextFormMode = checked ? FORM_MODES.CPF : FORM_MODES.CNPJ;
      const nextValues = mapBankAccountFieldsBr({
        accounts,
        bankListData: banksData,
        formMode: nextFormMode,
        t,
      });

      isSyncingRef.current = true;
      previousBankIdRef.current = nextValues?.bank?.value ?? null;
      previousAccountTypeValueRef.current =
        nextValues?.accountType?.value ?? null;

      setFormMode(nextFormMode);
      reset(nextValues);
      clearErrors();

      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    },
    [accounts, banksData, requiresCnpj, reset, clearErrors, setFormMode, t]
  );

  const isLoadBlocking = isSubmitting || isSavingData || isLoadingExistingData;
  const isSaveDisabled = isLoadBlocking || isLoadingDataBankList;
  const isLoadingBankData = isLoadingDataBankList || isLoadingExistingData;
  const showNoCnpjSwitch = !isLoadingExistingData && !requiresCnpj;

  return {
    isLoadBlocking,
    isSaveDisabled,
    isLoadingBankData,
    handleNoCnpjChange,
    showNoCnpjSwitch,
  };
};
